import type { PromptDefinition } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
  Badge,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Drawer,
  useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';

import { toaster } from '#src/components/ui/toaster';
import { UpsertPromptDrawer } from '#src/components/upsert-prompt-drawer';
import { DELETE_PROMPT } from '#src/graphql/mutations/delete-prompt';
import { GET_PROMPTS } from '#src/graphql/queries/prompts';

export function PromptsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPromptId, setEditingPromptId] = useState<string | undefined>();
  const [isUpsertDrawerOpen, setIsUpsertDrawerOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<PromptDefinition | null>(
    null,
  );

  const { data, loading, error, refetch } = useQuery(GET_PROMPTS);
  const [deletePrompt, { loading: isDeleting }] = useMutation(DELETE_PROMPT);
  const {
    open: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();

  const filteredPrompts =
    data?.prompts?.prompts?.filter((prompt) =>
      prompt.id.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const handleEditPrompt = (prompt: PromptDefinition) => {
    setEditingPromptId(prompt.id);
    setIsUpsertDrawerOpen(true);
  };

  const handleCreatePrompt = () => {
    setEditingPromptId(undefined);
    setIsUpsertDrawerOpen(true);
  };

  const handleCloseUpsertDrawer = () => {
    setEditingPromptId(undefined);
    setIsUpsertDrawerOpen(false);
  };

  const handleDeletePromptClick = (prompt: PromptDefinition) => {
    setPromptToDelete(prompt);
    onDeleteDialogOpen();
  };

  const handleConfirmDelete = async () => {
    if (!promptToDelete) return;

    try {
      const result = await deletePrompt({
        variables: {
          input: {
            id: promptToDelete.id,
          },
        },
      });

      if (result.data?.deletePrompt.success) {
        toaster.create({
          title: 'Success',
          description: result.data.deletePrompt.message,
          type: 'success',
          duration: 5000,
          closable: true,
        });
        refetch();
      } else {
        toaster.create({
          title: 'Error',
          description:
            result.data?.deletePrompt.message || 'Failed to delete prompt',
          type: 'error',
          duration: 5000,
          closable: true,
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Network error';
      toaster.create({
        title: 'Error',
        description: `Error deleting prompt: ${errorMessage}`,
        type: 'error',
        duration: 7000,
        closable: true,
      });
    } finally {
      onDeleteDialogClose();
      setPromptToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    onDeleteDialogClose();
    setPromptToDelete(null);
  };

  if (loading) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Saved Prompts
        </Heading>
        <Text>Loading prompts...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Saved Prompts
        </Heading>
        <Text color="red.500">Error loading prompts: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={4} align="stretch">
          <HStack>
            <Input
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              flex={1}
            />
            <Button onClick={() => refetch()} size="sm">
              Refresh
            </Button>
            <Button onClick={handleCreatePrompt} size="sm" colorScheme="blue">
              Create New Prompt
            </Button>
          </HStack>

          {filteredPrompts.length === 0 ? (
            <Text color="gray.500" textAlign="center" py={8}>
              {searchTerm
                ? 'No prompts match your search.'
                : 'No prompts saved yet.'}
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
              {filteredPrompts.map((prompt) => (
                <Card.Root key={prompt.id} variant="outline">
                  <CardHeader pb={2}>
                    <HStack justify="space-between" align="start">
                      <Text fontWeight="semibold" fontSize="md">
                        {prompt.id}
                      </Text>
                      <HStack gap={2}>
                        <Badge
                          colorScheme="green"
                          variant="subtle"
                          fontSize="xs"
                        >
                          {new Date(prompt.createdAt).toLocaleDateString()}
                        </Badge>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeletePromptClick(prompt)}
                          disabled={isDeleting}
                          title="Delete prompt"
                        >
                          🗑️
                        </Button>
                      </HStack>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Text fontSize="sm" color="gray.600" mb={4} lineClamp={3}>
                      {prompt.value}
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      width="full"
                      onClick={() => handleEditPrompt(prompt)}
                    >
                      Edit
                    </Button>
                  </CardBody>
                </Card.Root>
              ))}
            </SimpleGrid>
          )}
        </VStack>
      </Box>

      {/* Edit/Create Prompt Drawer */}
      <UpsertPromptDrawer
        open={isUpsertDrawerOpen}
        promptId={editingPromptId}
        onClose={handleCloseUpsertDrawer}
      />

      {/* Delete Confirmation Dialog */}
      <Drawer.Root
        open={isDeleteDialogOpen}
        onOpenChange={onDeleteDialogClose}
        placement="end"
        size="sm"
      >
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header>
              <Heading size="md" color="red.500">
                Delete Prompt
              </Heading>
            </Drawer.Header>
            <Drawer.Body>
              <Text>
                Are you sure you want to delete the prompt "
                <Text as="span" fontWeight="semibold">
                  {promptToDelete?.id}
                </Text>
                "? This action cannot be undone.
              </Text>
            </Drawer.Body>
            <Drawer.Footer>
              <HStack gap={2}>
                <Button
                  onClick={handleCancelDelete}
                  variant="outline"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmDelete}
                  colorScheme="red"
                  loading={isDeleting}
                  disabled={isDeleting}
                >
                  Delete
                </Button>
              </HStack>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    </Box>
  );
}
